import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Calendar, User } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/tareas")({
  head: () => ({ meta: [{ title: "Tareas — Admin" }] }),
  component: TareasPage,
});

type Task = {
  id: string;
  title: string;
  done: boolean;
  due_date: string | null;
  lead_id: string | null;
  created_at: string;
  lead?: { id: string; contact: { full_name: string } | null } | null;
};

function TareasPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "pending" | "done" | "overdue">("pending");
  const [newTitle, setNewTitle] = useState("");
  const [newDue, setNewDue] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select("id, title, done, due_date, lead_id, created_at, lead:leads(id, contact:contacts(full_name))")
        .order("done", { ascending: true })
        .order("due_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as Task[];
    },
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (filter === "all") return list;
    if (filter === "pending") return list.filter((t) => !t.done);
    if (filter === "done") return list.filter((t) => t.done);
    return list.filter((t) => !t.done && t.due_date && new Date(t.due_date) < today);
  }, [data, filter, today]);

  const toggle = async (t: Task) => {
    const prev = qc.getQueryData<Task[]>(["tasks"]);
    qc.setQueryData<Task[]>(["tasks"], (old) =>
      (old ?? []).map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)),
    );
    const { error } = await supabase.from("tasks").update({ done: !t.done }).eq("id", t.id);
    if (error) {
      qc.setQueryData(["tasks"], prev);
      toast.error(error.message);
    }
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("tasks").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const { error } = await supabase.from("tasks").insert({
      title: newTitle.trim(),
      due_date: newDue || null,
    });
    if (error) toast.error(error.message);
    else {
      setNewTitle("");
      setNewDue("");
      qc.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Tarea creada");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Tareas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestioná tus pendientes y follow-ups de leads.
          </p>
        </div>
      </div>

      {/* Quick add */}
      <form onSubmit={add} className="mt-6 flex flex-wrap gap-2 rounded-xl border border-border bg-card p-3">
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Nueva tarea…"
          className="min-w-[240px] flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={newDue}
          onChange={(e) => setNewDue(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
        >
          <Plus className="h-4 w-4" /> Agregar
        </button>
      </form>

      {/* Filter tabs */}
      <div className="mt-6 flex gap-1 border-b border-border">
        {(["pending", "overdue", "done", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-medium transition ${
              filter === f
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {f === "pending" ? "Pendientes" : f === "done" ? "Completadas" : f === "overdue" ? "Vencidas" : "Todas"}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-10 text-center text-sm text-muted-foreground">
            {filter === "pending" ? "¡Todo al día!" : "Sin tareas."}
          </div>
        )}
        {filtered.map((t) => {
          const overdue =
            !t.done && t.due_date && new Date(t.due_date) < today;
          return (
            <div
              key={t.id}
              className={`group flex items-center gap-3 rounded-lg border border-border bg-card p-3 transition ${
                t.done ? "opacity-60" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={t.done}
                onChange={() => toggle(t)}
                className="h-4 w-4 shrink-0 accent-primary cursor-pointer"
              />
              <div className="flex-1">
                <div className={`text-sm ${t.done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                  {t.title}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {t.due_date && (
                    <span className={`inline-flex items-center gap-1 ${overdue ? "text-destructive font-medium" : ""}`}>
                      <Calendar className="h-3 w-3" />
                      {new Date(t.due_date).toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                  {t.lead?.contact?.full_name && (
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {t.lead.contact.full_name}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => remove(t.id)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
